import { IsoDateTime, Uuid } from "@secretaa/http-contract";
import { z } from "zod";
import { Actor, Effect, Pointer, Ref, Scope, Version } from "../work-os/primitives.ts";
import { FieldStatus, FieldType } from "../dynamic-data.ts";

// Snapshot tạo bởi adapter owner sau ACL, không nhận từ AI/browser như giấy phép.
// datetime chỉ từ READ_MODEL/ARTIFACT; không thêm kiểu vào Dynamic Data v1.
export const SurfaceValueType = z.union([FieldType, z.literal("datetime")]);
export const SurfaceFieldMetadata = z.object({ id: Uuid, type: SurfaceValueType,
  status: FieldStatus, readable: z.boolean(), writable: z.boolean(), indexed: z.boolean(),
}).strict();
export const SurfaceMetadataSnapshot = z.object({
  ref: Ref, scope: Scope, actor: Actor, rendererVersion: Version, capturedAt: IsoDateTime, expiresAt: IsoDateTime,
  policyRef: Ref, blueprintManifestRef: Ref.nullable(),
  sources: z.array(z.object({ ref: Ref, scope: Scope, contractRef: Ref, fields: z.array(SurfaceFieldMetadata).max(100),
    kind: z.enum(["PROJECT_QUERY", "READ_MODEL", "ARTIFACT"]), maxRows: z.number().int().min(1).max(200),
  }).strict()).max(16),
  actions: z.array(z.object({ ref: Ref, scope: Scope, kind: z.enum(["COMMAND", "CAPABILITY", "FLOW"]), inputContractRef: Ref,
    targetConstraintRef: Ref, effect: Effect, confirmationPolicyRef: Ref,
    inputFields: z.array(z.object({ path: Pointer, type: SurfaceValueType, required: z.boolean() }).strict()).max(100),
  }).strict()).max(32),
}).strict().superRefine((s, ctx) => {
  if (Date.parse(s.expiresAt) <= Date.parse(s.capturedAt)) ctx.addIssue({ code: "custom", path: ["expiresAt"], message: "INVALID_SNAPSHOT_EXPIRY" });
  for (const group of ["sources", "actions"] as const)
    if (new Set(s[group].map(x => x.ref.id + ":" + x.ref.version)).size !== s[group].length)
      ctx.addIssue({ code: "custom", path: [group], message: "AMBIGUOUS_METADATA" });
  for (const [i, source] of s.sources.entries()) {
    if (new Set(source.fields.map(f => f.id)).size !== source.fields.length)
      ctx.addIssue({ code: "custom", path: ["sources", i], message: "AMBIGUOUS_METADATA" });
    if (source.kind === "PROJECT_QUERY" && (source.scope.projectId === null || source.fields.some(f => f.type === "datetime")))
      ctx.addIssue({ code: "custom", path: ["sources", i], message: "PROJECT_QUERY_CONTRACT_MISMATCH" });
  }
  for (const [i, action] of s.actions.entries())
    if (new Set(action.inputFields.map(f => f.path)).size !== action.inputFields.length)
      ctx.addIssue({ code: "custom", path: ["actions", i], message: "AMBIGUOUS_METADATA" });
});
export type SurfaceMetadataSnapshot = z.infer<typeof SurfaceMetadataSnapshot>;
